import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { crmApi, CRM_STATUS_STYLE } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Search, Loader2, Trash2 } from "lucide-react";

const EMPTY_ITEM = { fabric_type: "", gsm: "", width: "", quantity: "", unit: "meters", design_ref: "", target_price: "" };

export default function Enquiries() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ sources: [], statuses: [], units: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([crmApi.stats(), crmApi.list({ status, search: search || undefined })]);
      setStats(s); setRows(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { crmApi.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openNew = () => {
    setForm({ customer: { company: "", contact_name: "", phone: "", email: "", city: "", state: "" },
      source: "Phone", target_delivery: "", notes: "", items: [{ ...EMPTY_ITEM }] });
    setOpen(true);
  };

  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.customer.company) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, items: form.items
        .filter((it) => it.fabric_type)
        .map((it) => ({ ...it, quantity: Number(it.quantity) || 0, target_price: it.target_price ? Number(it.target_price) : null })) };
      await crmApi.create(payload);
      toast.success("Enquiry created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const statCards = useMemo(() => ([
    { key: "open", label: "Open", value: stats.open ?? 0, tone: "text-slate-900" },
    { key: "Qualified", label: "Qualified", value: stats.Qualified ?? 0, tone: "text-blue-600" },
    { key: "Quoted", label: "Quoted", value: stats.Quoted ?? 0, tone: "text-indigo-600" },
    { key: "Won", label: "Won", value: stats.Won ?? 0, tone: "text-emerald-600" },
    { key: "conversion_pct", label: "Conversion", value: `${stats.conversion_pct ?? 0}%`, tone: "text-amber-600" },
  ]), [stats]);

  return (
    <div className="space-y-6" data-testid="crm-enquiries-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">CRM / Enquiries</h2>
          <p className="mt-1 text-sm text-slate-500">Capture and qualify incoming customer requirements.</p>
        </div>
        {can("crm", "create") && (
          <Button onClick={openNew} data-testid="new-enquiry-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus className="mr-2 h-4 w-4" />New Enquiry
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {statCards.map((c) => (
          <Card key={c.key} className="p-4" data-testid={`crm-stat-${c.key}`}>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className={`mt-1 font-heading text-2xl font-bold ${c.tone}`}>{c.value}</p>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search enquiry no. / company / contact" className="pl-9" data-testid="crm-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="crm-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Enquiry</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Company</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Requirement</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Source</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/crm/${r.id}`)} data-testid={`enquiry-row-${r.enquiry_no}`}>
                    <TableCell className="font-medium text-slate-800">{r.enquiry_no}</TableCell>
                    <TableCell className="text-slate-700">{r.customer?.company}<span className="block text-xs text-slate-400">{r.customer?.contact_name}</span></TableCell>
                    <TableCell className="text-slate-600">{r.items?.length ? `${r.items[0].fabric_type} · ${r.items[0].quantity} ${r.items[0].unit}${r.items.length > 1 ? ` +${r.items.length - 1}` : ""}` : "—"}</TableCell>
                    <TableCell className="text-slate-500">{r.source}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${CRM_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                    <TableCell className="text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="crm-empty">No enquiries yet. Create your first enquiry to start the pipeline.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>New Enquiry</SheetTitle>
            <SheetDescription>Record the customer requirement. An enquiry number is generated automatically.</SheetDescription>
          </SheetHeader>
          {form && (
            <div className="mt-6 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5"><Label>Company *</Label>
                  <Input value={form.customer.company} onChange={(e) => setForm({ ...form, customer: { ...form.customer, company: e.target.value } })} data-testid="enq-company-input" /></div>
                <div className="space-y-1.5"><Label>Contact</Label>
                  <Input value={form.customer.contact_name} onChange={(e) => setForm({ ...form, customer: { ...form.customer, contact_name: e.target.value } })} data-testid="enq-contact-input" /></div>
                <div className="space-y-1.5"><Label>Phone</Label>
                  <Input value={form.customer.phone} onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input value={form.customer.email} onChange={(e) => setForm({ ...form, customer: { ...form.customer, email: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label>City</Label>
                  <Input value={form.customer.city} onChange={(e) => setForm({ ...form, customer: { ...form.customer, city: e.target.value } })} /></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger data-testid="enq-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Target delivery</Label>
                  <Input type="date" value={form.target_delivery} onChange={(e) => setForm({ ...form, target_delivery: e.target.value })} /></div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label>Requirement items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="enq-add-item-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
                </div>
                <div className="space-y-3">
                  {form.items.map((it, i) => (
                    <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`enq-item-${i}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Fabric type" value={it.fabric_type} onChange={(e) => setItem(i, "fabric_type", e.target.value)} data-testid={`enq-item-fabric-${i}`} />
                        <Input placeholder="GSM" value={it.gsm} onChange={(e) => setItem(i, "gsm", e.target.value)} />
                        <Input placeholder="Width" value={it.width} onChange={(e) => setItem(i, "width", e.target.value)} />
                        <Input placeholder="Qty" type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`enq-item-qty-${i}`} />
                        <Select value={it.unit} onValueChange={(v) => setItem(i, "unit", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{meta.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Target price/unit" type="number" value={it.target_price} onChange={(e) => setItem(i, "target_price", e.target.value)} />
                      </div>
                      {form.items.length > 1 && (
                        <button className="mt-2 flex items-center gap-1 text-xs text-rose-600" onClick={() => removeItem(i)}><Trash2 className="h-3.5 w-3.5" />Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5"><Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
            </div>
          )}
          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-enquiry-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create enquiry
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
